import React, { useState, useEffect } from 'react';
import { Copy, Check, Clock, FileText } from 'lucide-react';

const API_URL = 'https://snippets.secure.fastdns.io/api';

export default function App() {
    const [mode, setMode] = useState('create');
    const [markdown, setMarkdown] = useState('');
    const [expiryHours, setExpiryHours] = useState(1);
    const [shareUrl, setShareUrl] = useState('');
    const [copied, setCopied] = useState(false);
    const [currentSnippet, setCurrentSnippet] = useState(null);
    const [activeTab, setActiveTab] = useState('edit');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadSnippet = async (id) => {
            setLoading(true);
            try {
                const response = await fetch(`${API_URL}/snippets/${id}`);
                const data = await response.json();

                if (response.ok) {
                    if (data.isExpired) {
                        setMode('expired');
                    } else {
                        setCurrentSnippet(data);
                        setMarkdown(data.markdown);
                        setMode('view');
                    }
                } else {
                    setMode('notfound');
                }
            } catch (error) {
                console.error('Error loading snippet:', error);
                setMode('error');
            } finally {
                setLoading(false);
            }
        };

        const params = new URLSearchParams(window.location.search);
        const snippetId = params.get('s');
        if (snippetId) {
            loadSnippet(snippetId);
        }
    }, []);

    const createOrUpdateSnippet = async () => {
        setLoading(true);
        try {
            // Check if we're updating an existing snippet
            const params = new URLSearchParams(window.location.search);
            const existingId = params.get('s');

            let response, data;

            if (existingId && shareUrl) {
                // Update existing snippet
                response = await fetch(`${API_URL}/snippets/${existingId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ markdown, expiryHours })
                });

                data = await response.json();

                if (response.ok) {
                    setShareUrl(data.url);
                    setCurrentSnippet(data);
                } else {
                    alert('Error updating snippet: ' + data.error);
                }
            } else {
                // Create new snippet
                response = await fetch(`${API_URL}/snippets`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ markdown, expiryHours })
                });

                data = await response.json();

                if (response.ok) {
                    setShareUrl(data.url);
                } else {
                    alert('Error creating snippet: ' + data.error);
                }
            }
        } catch (error) {
            console.error('Error creating/updating snippet:', error);
            alert('Failed to save snippet. Make sure the backend is running.');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const renderMarkdown = (text) => {
        const lines = text.split('\n');
        const elements = [];
        let inCodeBlock = false;
        let codeLines = [];
        let language = '';
        let codeBlockIndex = 0;

        lines.forEach((line, idx) => {
            if (line.startsWith('```')) {
                if (inCodeBlock) {
                    const codeContent = codeLines.join('\n');
                    const blockId = `code-block-${codeBlockIndex}`;
                    elements.push(
                        <div key={idx} className="relative group my-4">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                <code className={`language-${language}`}>{codeContent}</code>
              </pre>
                            <button
                                onClick={() => copyToClipboard(codeContent)}
                                className="absolute top-2 right-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
                                title="Copy code"
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-3 h-3" />
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-3 h-3" />
                                        Copy
                                    </>
                                )}
                            </button>
                        </div>
                    );
                    codeLines = [];
                    language = '';
                    inCodeBlock = false;
                    codeBlockIndex++;
                } else {
                    language = line.substring(3).trim();
                    inCodeBlock = true;
                }
            } else if (inCodeBlock) {
                codeLines.push(line);
            } else if (line.startsWith('# ')) {
                elements.push(<h1 key={idx} className="text-3xl font-bold mt-6 mb-4">{line.substring(2)}</h1>);
            } else if (line.startsWith('## ')) {
                elements.push(<h2 key={idx} className="text-2xl font-bold mt-5 mb-3">{line.substring(3)}</h2>);
            } else if (line.startsWith('### ')) {
                elements.push(<h3 key={idx} className="text-xl font-bold mt-4 mb-2">{line.substring(4)}</h3>);
            } else if (line.trim() === '') {
                elements.push(<div key={idx} className="h-4"></div>);
            } else {
                elements.push(<p key={idx} className="mb-2">{line}</p>);
            }
        });

        return elements;
    };

    const getTimeRemaining = (expiresAt) => {
        const remaining = new Date(expiresAt) - Date.now();
        const hours = Math.floor(remaining / (60 * 60 * 1000));
        const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
        return `${hours}h ${minutes}m`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-8 flex items-center justify-center">
                <div className="text-xl text-gray-600">Loading...</div>
            </div>
        );
    }

    if (mode === 'expired') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 p-8 flex items-center justify-center">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
                    <Clock className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Snippet Expired</h2>
                    <p className="text-gray-600">This code snippet is no longer available.</p>
                </div>
            </div>
        );
    }

    if (mode === 'notfound' || mode === 'error') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 p-8 flex items-center justify-center">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Snippet Not Found</h2>
                    <p className="text-gray-600">This snippet doesn't exist or has been deleted.</p>
                </div>
            </div>
        );
    }

    if (mode === 'view' && currentSnippet) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-8">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-xl shadow-lg p-8">
                        <div className="flex justify-between items-center mb-6">
                            <div className="flex items-center gap-2 text-gray-600">
                                <Clock className="w-5 h-5" />
                                <span className="text-sm">Expires in {getTimeRemaining(currentSnippet.expiresAt)}</span>
                            </div>
                            <button
                                onClick={() => copyToClipboard(currentSnippet.markdown)}
                                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                {copied ? 'Copied!' : 'Copy Code'}
                            </button>
                        </div>

                        <div className="prose max-w-none">
                            {renderMarkdown(currentSnippet.markdown)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-8">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <FileText className="w-8 h-8 text-indigo-600" />
                        <h1 className="text-3xl font-bold text-gray-800">Snippet Sharer</h1>
                    </div>

                    {!shareUrl ? (
                        <>
                            <div className="mb-6">
                                <div className="flex gap-2 mb-3">
                                    <button
                                        onClick={() => setActiveTab('edit')}
                                        className={`px-4 py-2 rounded-t-lg font-medium transition ${
                                            activeTab === 'edit'
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('preview')}
                                        className={`px-4 py-2 rounded-t-lg font-medium transition ${
                                            activeTab === 'preview'
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                    >
                                        Preview
                                    </button>
                                </div>

                                {activeTab === 'edit' ? (
                                    <textarea
                                        value={markdown}
                                        onChange={(e) => setMarkdown(e.target.value)}
                                        placeholder="Enter your markdown here...&#10;&#10;Example:&#10;# Interview Task&#10;&#10;```javascript&#10;function example() {&#10;  return 'Hello World';&#10;}&#10;```"
                                        className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                                    />
                                ) : (
                                    <div className="w-full min-h-64 p-4 border border-gray-300 rounded-lg bg-gray-50">
                                        {markdown.trim() ? (
                                            <div className="prose max-w-none">
                                                {renderMarkdown(markdown)}
                                            </div>
                                        ) : (
                                            <p className="text-gray-400 italic">Nothing to preview yet. Switch to Edit tab to add content.</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Expiry Time (hours)
                                </label>
                                <input
                                    type="number"
                                    value={expiryHours}
                                    onChange={(e) => setExpiryHours(Math.max(0.25, parseFloat(e.target.value)))}
                                    min="0.25"
                                    step="0.25"
                                    className="w-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                />
                            </div>

                            <button
                                onClick={createOrUpdateSnippet}
                                disabled={!markdown.trim() || loading}
                                className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Saving...' : (shareUrl ? 'Update Snippet' : 'Generate Share Link')}
                            </button>
                        </>
                    ) : (
                        <div className="space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                <p className="text-green-800 font-medium mb-3">✓ Snippet created successfully!</p>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={shareUrl}
                                        readOnly
                                        className="flex-1 p-3 bg-white border border-gray-300 rounded-lg font-mono text-sm"
                                    />
                                    <button
                                        onClick={() => copyToClipboard(shareUrl)}
                                        className="px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
                                    >
                                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                        {copied ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-600 mt-3">
                                    ⏱ Expires in {expiryHours} hour{expiryHours !== 1 ? 's' : ''}
                                </p>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShareUrl('');
                                        setMode('create');
                                    }}
                                    className="flex-1 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition"
                                >
                                    ← Back to Edit
                                </button>
                                <button
                                    onClick={() => {
                                        setShareUrl('');
                                        setMarkdown('');
                                        setExpiryHours(1);
                                        setMode('create');
                                        window.history.pushState({}, '', window.location.pathname);
                                    }}
                                    className="flex-1 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition"
                                >
                                    Create Another Snippet
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-3">Markdown Tips</h2>
                    <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Use <code className="bg-gray-100 px-1 rounded">```language</code> for code blocks (e.g., ```javascript, ```python)</li>
                        <li>• Use <code className="bg-gray-100 px-1 rounded"># Heading</code> for headings</li>
                        <li>• Simple paragraphs work as-is</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}